import React, { ChangeEvent, useMemo, useState } from "react";

import { useAdminAuth } from "../auth/AdminAuthProvider";
import { AdminProtectedRoute } from "../auth/AdminProtectedRoute";
import { AdminShell } from "../components/AdminShell";
import { HistorySection } from "../components/site-content/HistorySection";
import { IRSection } from "../components/site-content/IRSection";
import { PopupSection } from "../components/site-content/PopupSection";
import { YoutubeSection } from "../components/site-content/YoutubeSection";
import { InfoBanner, LoadingLine, MetaStateBar, Modal, NoticeBanner, Pager, PillTabBar, SectionCard } from "../components/ui";
import { useFirebaseUploadGuard } from "../hooks/useFirebaseUploadGuard";
import { SiteContentEditorTab, useSiteContentEditor } from "../hooks/useSiteContentEditor";

const tabItems: Array<{ key: SiteContentEditorTab; label: string }> = [
  { key: "youtube", label: "YouTube" },
  { key: "popup", label: "Popup" },
  { key: "ir_ko", label: "IR" },
  { key: "history", label: "History" },
];

/**
 * 사이트 콘텐츠 관리자 메인 페이지.
 * 탭별 편집 섹션과 미리보기 모달을 조합해 단일 편집 흐름을 제공한다.
 */
export function SiteContentPage() {
  const { getIdToken, ready, user } = useAdminAuth();
  const [youtubePreviewOpen, setYoutubePreviewOpen] = useState(false);
  const [popupPreviewId, setPopupPreviewId] = useState<string | null>(null);
  const [historyPreviewOpen, setHistoryPreviewOpen] = useState(false);
  const [historyPreviewQuery, setHistoryPreviewQuery] = useState("");

  const editor = useSiteContentEditor({ ready, user, getIdToken });
  const uploadGuard = useFirebaseUploadGuard(user);

  // History 미리보기 전용 검색/페이지네이션 데이터를 계산한다.
  const historyPreview = useMemo(() => {
    const list = editor.getHistoryPreviewItems();
    const query = historyPreviewQuery.trim().toLowerCase();
    const filtered = query
      ? list.filter((item) => `${item.year} ${(item.contents || []).join(" ")}`.toLowerCase().includes(query))
      : list;
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const page = Math.min(Math.max(1, editor.historyPreviewPage), totalPages);
    const start = (page - 1) * pageSize;
    return {
      rows: filtered.slice(start, start + pageSize),
      totalPages,
      page,
    };
  }, [editor, historyPreviewQuery]);

  // 현재 선택된 popup 슬롯의 미리보기 데이터를 찾는다.
  const popupPreview = useMemo(() => editor.sections.popup.find((popup) => popup.id === popupPreviewId) || null, [editor.sections.popup, popupPreviewId]);
  const irPagedRows = editor.getIrPreviewRows();
  const historyPagedRows = editor.getHistoryRowsForPage();

  return (
    <AdminProtectedRoute>
      <AdminShell
        title="홈페이지 콘텐츠 관리"
        description="YouTube / Popup / IR / History 탭에서 콘텐츠를 편집하고 저장합니다."
        pageClassName="cm-site-content-legacy-spacing"
        titleActions={
          <div className="cm-actions">
            <button className="cm-btn cm-btn-refresh" disabled={editor.loading} onClick={() => void editor.reload()} type="button">
              새로고침
            </button>
          </div>
        }
      >
        <MetaStateBar
          items={[
            {
              key: "dirty",
              value: editor.metaState.dirty ? "변경 사항 있음" : "저장 상태",
              tone: editor.metaState.dirty ? "dirty" : "ok",
            },
            {
              key: "touched",
              value: `변경 시각: ${editor.metaState.lastTouchedAt || "-"}`,
              tone: "default",
            },
            {
              key: "saved",
              value: `저장 시각: ${editor.metaState.savedAt || "-"}`,
              tone: "default",
            },
          ]}
        />

        <InfoBanner>
          로그인 관리자: <strong>{user?.email || user?.uid || "-"}</strong>
          {!uploadGuard.canUpload ? <div style={{ marginTop: 6 }}>{uploadGuard.reason}</div> : null}
        </InfoBanner>

        <div className="cm-actions cm-save-all-actions">
          <button
            className="cm-btn"
            disabled={editor.savingKey === "all"}
            onClick={() => {
              if (!window.confirm("전체 저장을 진행하겠습니까?")) return;
              void editor.saveAll();
            }}
            type="button"
          >
            {editor.savingKey === "all" ? "저장 중..." : "전체 저장"}
          </button>
        </div>

        <NoticeBanner notice={editor.notice} />

        <PillTabBar active={editor.tab} items={tabItems} onChange={editor.setTab} />

        {editor.loading ? <LoadingLine message="site_config를 불러오는 중입니다..." /> : null}
        {!editor.loading && editor.syncing ? <LoadingLine message="서버 최신 정보를 동기화 중입니다..." /> : null}

        <div hidden={editor.tab !== "youtube"}>
          <YoutubeSection
            getRowsForPage={editor.getYoutubeRowsForPage}
            getSummary={editor.getYoutubeSummary}
            invalidUrls={{
              about: editor.youtubeValidation.aboutInvalid,
              videos: editor.youtubeValidation.videosInvalid,
            }}
            onPageChange={editor.setYoutubePage}
            onChangeRow={editor.updateYoutubeRow}
            onAddRow={editor.appendYoutubeRow}
            onRemoveRow={editor.removeYoutubeRow}
            onReorderRows={editor.reorderYoutubeRows}
            onOpenPreview={() => setYoutubePreviewOpen(true)}
            onSave={() => void editor.saveSection("youtube")}
            saving={editor.savingKey === "youtube" || editor.savingKey === "all"}
          />
        </div>

        <div hidden={editor.tab !== "popup"}>
          <PopupSection
            popups={editor.sections.popup}
            files={editor.popupFiles}
            canUpload={uploadGuard.canUpload}
            uploadGuardReason={uploadGuard.reason}
            onUpdate={editor.updatePopup}
            onFileSelect={(popupId, file) => {
              editor.setPopupFiles((prev) => ({ ...prev, [popupId]: file }));
              editor.markDirty();
            }}
            onSave={() => void editor.saveSection("popup")}
            onOpenPreview={(popupId) => setPopupPreviewId(popupId)}
            saving={editor.savingKey === "popup" || editor.savingKey === "all"}
          />
        </div>

        <div hidden={editor.tab !== "ir_ko"}>
          <IRSection
            rows={irPagedRows.rows}
            page={irPagedRows.page}
            totalPages={irPagedRows.totalPages}
            start={irPagedRows.start}
            files={editor.irFiles}
            canUpload={uploadGuard.canUpload}
            uploadGuardReason={uploadGuard.reason}
            onPageChange={editor.setIrPage}
            onUpdate={editor.updateIr}
            onFileSelect={(itemId, file) => {
              editor.setIrFiles((prev) => ({ ...prev, [itemId]: file }));
              editor.markDirty();
              if (file) {
                editor.saveIrPublishedAt(editor.sections.ir_ko.items.findIndex((item) => item.id === itemId));
              }
            }}
            onAdd={editor.addIrItem}
            onRemove={editor.removeIrItem}
            onReorder={editor.reorderIrItems}
            onSave={() => void editor.saveSection("ir_ko")}
            saving={editor.savingKey === "ir_ko" || editor.savingKey === "all"}
          />
        </div>

        <div hidden={editor.tab !== "history"}>
          <HistorySection
            rows={historyPagedRows.rows}
            page={historyPagedRows.page}
            totalPages={historyPagedRows.totalPages}
            start={historyPagedRows.start}
            onPageChange={editor.setHistoryPage}
            onAddItem={editor.addHistoryItem}
            onRemoveItem={editor.removeHistoryItem}
            onUpdateMonth={editor.updateHistoryMonth}
            onUpdateLine={editor.updateHistoryLine}
            onAddLine={editor.addHistoryLine}
            onRemoveLine={editor.removeHistoryLine}
            onReorderCards={editor.reorderHistoryCards}
            onReorderLines={editor.reorderHistoryLines}
            onOpenPreview={() => setHistoryPreviewOpen(true)}
            onSave={() => void editor.saveSection("history")}
            saving={editor.savingKey === "history" || editor.savingKey === "all"}
          />
        </div>

        <Modal onClose={() => setYoutubePreviewOpen(false)} open={youtubePreviewOpen} title="YouTube 미리보기">
          <div className="cm-grid-2">
            {(["about", "videos"] as const).map((category) => (
              <SectionCard key={category} title={category === "about" ? "AboutUs 페이지" : "Videos 페이지"}>
                <div className="cm-youtube-preview-scroll">
                  {editor.getYoutubePreview(category).map((url, index) => (
                    <div className="cm-preview cm-preview-embed" key={`${url}-${index}`}>
                      <iframe src={url} style={{ width: "100%", minHeight: 220, border: 0, borderRadius: 8 }} title={`${category}-${index}`} />
                    </div>
                  ))}
                </div>
                {!editor.getYoutubePreview(category).length ? <div className="cm-muted">표시할 링크가 없습니다.</div> : null}
              </SectionCard>
            ))}
          </div>
        </Modal>

        <Modal onClose={() => setPopupPreviewId(null)} open={!!popupPreview} title={`${popupPreview?.id || "popup"} 미리보기`} width={520}>
          {popupPreview ? (
            <div className="cm-list-card" style={{ maxWidth: 420, margin: "0 auto" }}>
              <div className="cm-inline-row" style={{ justifyContent: "space-between" }}>
                <span className={`cm-pill ${popupPreview.enabled ? "ok" : "warn"}`.trim()}>{popupPreview.enabled ? "활성" : "비활성"}</span>
                <button className="cm-btn cm-btn-secondary cm-btn-icon" type="button">
                  X
                </button>
              </div>

              {popupPreview.image_url ? (
                <div className="cm-fixed-preview-box">
                  <img alt={popupPreview.id} className="cm-fixed-preview-image" src={popupPreview.image_url} />
                </div>
              ) : (
                <div className="cm-muted">이미지 없음</div>
              )}

              <div className="cm-field">
                <label>링크</label>
                <input className="cm-input" readOnly value={popupPreview.link_url || ""} />
              </div>

              <label className="cm-inline-row">
                <input type="checkbox" />
                <span>오늘 하루 보지 않기</span>
              </label>
            </div>
          ) : null}
        </Modal>

        <Modal onClose={() => setHistoryPreviewOpen(false)} open={historyPreviewOpen} title="History 미리보기">
          <div className="cm-toolbar">
            <input
              className="cm-input"
              onChange={(event: ChangeEvent<HTMLInputElement>) => setHistoryPreviewQuery(event.target.value)}
              placeholder="연월/내용 검색"
              value={historyPreviewQuery}
            />
          </div>
          <div className="cm-panel-grid">
            {historyPreview.rows.map((item) => (
              <SectionCard key={item.id} title={item.year}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {(item.contents || []).map((line, index) => (
                    <li key={`${item.id}-${index}`}>{line}</li>
                  ))}
                </ul>
              </SectionCard>
            ))}
            {!historyPreview.rows.length ? <div className="cm-muted">조건에 맞는 연혁이 없습니다.</div> : null}
          </div>
          <Pager
            page={historyPreview.page}
            totalPages={historyPreview.totalPages}
            onPageChange={(page) => {
              editor.setHistoryPreviewPage(page);
            }}
          />
        </Modal>
      </AdminShell>
    </AdminProtectedRoute>
  );
}
